import { useEffect, useState } from "react";

import { InfoMessageDto, MenuSettingsDto, StaffCallKindDto } from "services/menu/restModels";

import { PRIMARY_LOCALE, STAFF_CALL_TYPES } from "features/MenuSettings/constants";
import { MenuSettingsFlags } from "features/MenuSettings/types";

const DEFAULT_FLAGS: MenuSettingsFlags = {
    isMenuEnabled: true,
    isCartEnabled: false,
    isOrderCreationEnabled: true,
    isPromoCodesEnabled: true,
    isLoyaltyProgramEnabled: false,
};

const EMPTY_INFO_MESSAGE: InfoMessageDto = {
    isEnabled: false,
    title: {},
    description: {},
    callToAction: {},
    mediaUrl: "",
    mediaType: "image",
};

type InfoMessageTextField = "title" | "description" | "callToAction";

export const useMenuFlagsState = (settings: MenuSettingsDto | null) => {
    const [flags, setFlags] = useState<MenuSettingsFlags>(DEFAULT_FLAGS);
    const [staffCalls, setStaffCalls] = useState<StaffCallKindDto[]>([]);
    const [enabledLocales, setEnabledLocales] = useState<string[]>([PRIMARY_LOCALE]);
    const [infoMessage, setInfoMessage] = useState<InfoMessageDto>(EMPTY_INFO_MESSAGE);

    useEffect(() => {
        if (!settings) return;

        setFlags({
            isMenuEnabled: settings.isMenuEnabled,
            isCartEnabled: settings.isCartEnabled,
            isOrderCreationEnabled: settings.isOrderCreationEnabled,
            isPromoCodesEnabled: settings.isPromoCodesEnabled,
            isLoyaltyProgramEnabled: settings.isLoyaltyProgramEnabled,
        });
        setStaffCalls(settings.staffCalls || []);
        setEnabledLocales(settings.availableLanguages.enabledLocales);
        setInfoMessage(settings.infoMessage);
    }, [settings]);

    const setFlag = (flag: keyof MenuSettingsFlags, value: boolean) =>
        setFlags((prev) => ({ ...prev, [flag]: value }));

    /*
    * Набор и есть выключатель вызовов: пустой список — кнопки у гостя нет. Порядок
    * держим по каталогу, он же порядок кнопок в меню.
    * */
    const toggleStaffCall = (kind: StaffCallKindDto, enabled: boolean) =>
        setStaffCalls((prev) => {
            const next = enabled ? [...prev, kind] : prev.filter((item) => item !== kind);
            const catalog = STAFF_CALL_TYPES.map((item) => item.kind);
            /*
            * Вид, которого ещё нет в каталоге кабинета (бэк добавил раньше фронта),
            * уезжает в конец, но остаётся включённым: фильтрация по каталогу стёрла бы
            * настройку заведения при первом же переключении соседнего тумблера.
            * */
            const order = (item: StaffCallKindDto) => {
                const index = catalog.indexOf(item);

                return index < 0 ? catalog.length : index;
            };

            return next.slice().sort((a, b) => order(a) - order(b));
        });

    /*
    * Русский не выключается: бэк отклоняет пустой список языков, а гостевое меню без
    * базового языка осталось бы без текстов.
    * */
    const toggleLocale = (locale: string, enabled: boolean) => {
        if (locale === PRIMARY_LOCALE) return;

        setEnabledLocales((prev) =>
            enabled ? [...prev, locale] : prev.filter((item) => item !== locale)
        );
    };

    const setInfoMessageEnabled = (isEnabled: boolean) =>
        setInfoMessage((prev) => ({ ...prev, isEnabled }));

    const setLocalizedInfoMessageField = (
        field: InfoMessageTextField,
        value: string,
        lang: string
    ) =>
        setInfoMessage((prev) => ({
            ...prev,
            [field]: { ...prev[field], [lang]: value },
        }));

    const setInfoMessageMediaUrl = (mediaUrl: string) =>
        setInfoMessage((prev) => ({ ...prev, mediaUrl }));

    return {
        flags,
        staffCalls,
        enabledLocales,
        infoMessage,
        setFlag,
        toggleStaffCall,
        toggleLocale,
        setInfoMessageEnabled,
        setLocalizedInfoMessageField,
        setInfoMessageMediaUrl,
    };
};

export type MenuFlagsState = ReturnType<typeof useMenuFlagsState>;
